import { flow, pipe } from 'fp-ts/lib/function';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { detailCodec, listCodec } from './codec';
import {
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  setBody,
  setQuery,
  Versioned,
} from '@/services/api';
import { addVersionForUpdate } from '../../request';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import { encodeScheduleQuery, ScheduleQuery } from '@/services/api/resources/schedule/query';
import { Schedule } from '@/entities/schedule';
import { intoSendable } from '@/services/api/resources/schedule/transform';
import * as q from '@/services/api/request/query';
import { filterCodec } from '@/services/api/resources/filter';

const baseUrl = '/v2/schedules';
const filterUrl = '/v2/filters/schedules';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;

export const filter = (preventEmptyTitle = false) => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  map(setQuery({ preventEmptyTitle: q.boolean(preventEmptyTitle) })),
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const list = (query: ScheduleQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeScheduleQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const create = (schedule: Schedule) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(intoSendable(schedule))),
  chain(fetchApi),
  chain(decodeId),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const update = (id: number, schedule: Versioned<Schedule>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(schedule.version)),
  map(setBody(intoSendable(schedule.data))),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);
